import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, FileText } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { inr } from "../lib/payRail";

const DELAYS = [0, 2000, 4000, 8000, 15000, 30000];

export default function PayReturn() {
  const { api } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const intentId = params.get("intent") || (() => { try { return sessionStorage.getItem("vinci.pay.intent"); } catch { return null; } })();
  const [intent, setIntent] = useState(null);
  const [error, setError] = useState("");
  const attempt = useRef(0);

  useEffect(() => {
    if (!intentId) { setError("No payment reference found."); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.post(`/payments/intents/${intentId}/sync`);
        if (cancelled) return;
        setIntent(data.intent);
        if (data.intent.status === "pending" && attempt.current < DELAYS.length - 1) {
          attempt.current += 1;
          setTimeout(poll, DELAYS[attempt.current]);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not verify the payment.");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [api, intentId]);

  const status = intent?.status;
  const next = intent?.next_path || "/account";
  useEffect(() => {
    if (status === "paid") { const t = setTimeout(() => nav(next), 4000); return () => clearTimeout(t); }
  }, [status, next, nav]);

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0] grid place-items-center px-6" data-testid="pay-return-page">
      <div className="w-full max-w-lg border border-[#C9A84C]/30 bg-[#0D0D0D] rounded-sm p-8 text-center">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A84C] mb-6">Vinci Payment Rail · {intent?.provider === "razorpay" ? "Razorpay" : "PhonePe"}</div>
        {error && <div data-testid="pay-return-error" className="text-[#E08A8A]"><XCircle className="mx-auto mb-3" /> {error}</div>}
        {!error && !intent && <div data-testid="pay-return-loading"><Loader2 className="mx-auto animate-spin text-[#C9A84C]" /><p className="mt-3 text-sm text-[#888]">Confirming your payment with PhonePe…</p></div>}
        {intent && status === "pending" && <div data-testid="pay-return-pending"><Loader2 className="mx-auto animate-spin text-[#C9A84C]" /><p className="mt-3 text-sm text-[#D4D4D4]">Payment of {inr(intent.total_inr)} is still being confirmed. This page refreshes automatically.</p></div>}
        {intent && status === "paid" && (
          <div data-testid="pay-return-success">
            <CheckCircle2 size={40} className="mx-auto text-[#7FD1A5]" />
            <h1 className="font-serif text-2xl mt-4">Payment received</h1>
            <p className="text-sm text-[#D4D4D4] mt-2">{intent.title} · {inr(intent.total_inr)} {intent.gst_inr > 0 && <span className="text-[#888]">(incl. 18% GST {inr(intent.gst_inr)})</span>}</p>
            {intent.gst_invoice_no && <p className="mt-3 inline-flex items-center gap-2 text-xs text-[#C9A84C]" data-testid="pay-return-gst-invoice"><FileText size={13} /> GST Invoice {intent.gst_invoice_no} e-mailed to you</p>}
            <p className="text-xs text-[#888] mt-4">Reference {intent.ref}. Redirecting…</p>
            <Link to={next} data-testid="pay-return-continue" className="mt-5 inline-block rounded-sm bg-[#C9A84C] text-black px-6 py-2.5 text-sm font-semibold">Continue</Link>
          </div>
        )}
        {intent && status === "failed" && (
          <div data-testid="pay-return-failed">
            <XCircle size={40} className="mx-auto text-[#E08A8A]" />
            <h1 className="font-serif text-2xl mt-4">Payment not completed</h1>
            <p className="text-sm text-[#D4D4D4] mt-2">No money was captured for {intent.title}. You can try again from where you left off.</p>
            <Link to={next} data-testid="pay-return-retry" className="mt-5 inline-block rounded-sm border border-[#C9A84C] text-[#C9A84C] px-6 py-2.5 text-sm">Back & retry</Link>
          </div>
        )}
      </div>
    </div>
  );
}
